import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiMeta } from '../../../_core/_model/api-response';
import { MlPredictionDetailsDto, MlPredictionListItemDto } from '../../../_core/_model/machine-learning';
import { MachineLearningAdminService } from '../../../_core/_services/machine-learning-admin.service';
import { AuthService } from '../../../_core/_services/auth.service';
import { mlPredictionTypesForRole } from '../../../_core/_model/user-role';

@Component({
  selector: 'app-admin-ml-predictions',
  standalone: false,
  templateUrl: './admin-ml-predictions.html',
  styleUrl: './admin-ml-predictions.css'
})
export class AdminMlPredictions implements OnInit {
  loading = false;
  detailsLoading = false;
  error = '';
  items: MlPredictionListItemDto[] = [];
  meta: ApiMeta | null = null;
  selected: MlPredictionDetailsDto | null = null;

  filters = {
    predictionType: '',
    classification: '',
    provider: '',
    outcomeState: '',
    mode: ''
  };

  predictionTypes: string[] = [];

  constructor(
    private mlService: MachineLearningAdminService,
    private route: ActivatedRoute,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => {
      this.predictionTypes = mlPredictionTypesForRole(user?.role);

      const requestedType = this.route.snapshot.queryParamMap.get('predictionType');
      if (requestedType && this.predictionTypes.includes(requestedType)) {
        this.filters.predictionType = requestedType;
      }

      this.load(1);
    });
  }

  load(page = 1): void {
    this.loading = true;
    this.error = '';
    this.mlService.getPredictions({
      predictionType: this.filters.predictionType || null,
      classification: this.filters.classification || null,
      provider: this.filters.provider || null,
      hasOutcome: this.filters.outcomeState === 'labelled' ? true : this.filters.outcomeState === 'unlabelled' ? false : null,
      isShadowMode: this.filters.mode === 'shadow' ? true : this.filters.mode === 'live' ? false : null,
      page,
      rowsPerPage: 20
    }).subscribe({
      next: response => {
        this.items = response.data || [];
        this.meta = response.meta;
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.errorMessage(error, 'Unable to load prediction history.');
        this.items = [];
        this.meta = null;
        this.loading = false;
      }
    });
  }

  reset(): void {
    this.filters = { predictionType: '', classification: '', provider: '', outcomeState: '', mode: '' };
    this.load(1);
  }

  openDetails(id: string): void {
    this.detailsLoading = true;
    this.selected = null;
    this.mlService.getPrediction(id).subscribe({
      next: response => {
        this.selected = response.data;
        this.detailsLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.errorMessage(error, 'Unable to load prediction details.');
        this.detailsLoading = false;
      }
    });
  }

  closeDetails(): void { this.selected = null; }

  badgeClass(value?: string | null): string {
    switch ((value || '').toLowerCase()) {
      case 'low': case 'confirmednormal': return 'bg-success-subtle text-success';
      case 'moderate': case 'monitor': return 'bg-warning-subtle text-warning-emphasis';
      case 'high': return 'bg-orange-subtle text-orange';
      case 'critical': case 'falsepositive': case 'falsenegative': case 'confirmedaccounttakeover': return 'bg-danger-subtle text-danger';
      default: return 'bg-secondary-subtle text-secondary';
    }
  }

  prettyJson(value?: string | null): string {
    if (!value) return 'Not recorded';
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }

  private errorMessage(error: HttpErrorResponse, fallback: string): string {
    return error.error?.error?.details || error.error?.message || error.message || fallback;
  }
}
